import React from 'react';
import {
  Button,
  CheckPanel,
  Dialog,
  DialogContent,
  DialogFooter,
  SelectList,
} from '@taopix/taopix-design-system';
import {useTranslation} from 'react-i18next';
import {useAssistant} from '../../../Context/Assistant/AssistantContext';
import {AssistantActions} from '../../../Context/Assistant/AssistantActionTypes';
import {ListItemDataType} from '@taopix/taopix-design-system/dist/types/Components/SelectList/SelectList';
import {useTaopix} from '../../../Context/TaopixContext';
import {useExperience} from '../../../Experience/ExperienceProvider';
import useSettings from '../../../../Hooks/useSettings';
import {useUiState} from '../../../Context/UserInterface/UserInterfaceContext';
import {SubStepOptionProps} from './SubStep';
import {AIMode, AssistantType, Common} from '@taopix/taopix-core';
import {useSelector} from 'react-redux';
import {TotalPageCountSelector} from '../../../../Selectors';
import classNames from 'classnames';

const PageCountDropDown = ({
  hasPricedPaperComponent,
  disabled,
}: {
  hasPricedPaperComponent: boolean;
  disabled: boolean;
}) => {
  const taopix = useTaopix();
  const assistant = useAssistant();
  const {experience} = useExperience();
  const {t} = useTranslation(['assistants']);
  const {pageCountModeStep} = assistant.state.settings;
  const pageCount = useSelector(TotalPageCountSelector);

  const getPageCounts = React.useCallback(() => {
    const requiredPages = pageCountModeStep.maxPages - pageCountModeStep.minPages;
    const labelSingular = t('optionsandextras:str_LabelPage');
    const labelPlural = t('optionsandextras:str_LabelPages');

    let minCompatiblePageCount: number = pageCountModeStep.minPages;

    // AI only works with even page counts.
    let pageInsertCount: number = pageCountModeStep.step;

    if (pageInsertCount % 2 === 1) {
      pageInsertCount++;
    }

    // We need to make sure the first value is compatible with the page insert count.
    while (
      !isNaN(minCompatiblePageCount) &&
      !isNaN(pageInsertCount) &&
      minCompatiblePageCount % pageInsertCount !== 0
    ) {
      minCompatiblePageCount++;
    }

    // Build the list of pages to add.
    const pagesToAddList = Array.from(
      {length: Math.floor(requiredPages / pageInsertCount) + 1},
      (pageNumber, index) => {
        return minCompatiblePageCount + index * pageInsertCount;
      }
    );

    if (pagesToAddList.length === 0) {
      pagesToAddList.push(pageCountModeStep.defaultPages);
    }

    return pagesToAddList.map(pageCount => {
      let optionLabel = (1 === pageCount ? labelSingular : labelPlural).replace(
        '^0',
        pageCount.toString()
      );

      if (experience.WIZARD.header.price && hasPricedPaperComponent) {
        // Use the pricing engine to calculate the project price.
        const price = assistant
          .getPricingEngine(
            AssistantType.SmartDesign === experience.WIZARD.designAssistantType.designAssistantType
              ? AIMode.On
              : AIMode.Off
          )
          .calculateProjectPrice(pageCount, [], 0, 0);
        const formattedPrice = taopix.core
          .get('pricingEngine')
          .formatCurrencyNumber(price, assistant.currency);

        optionLabel = `${optionLabel} (${formattedPrice})`;
      }

      return {
        label: optionLabel,
        value: pageCount.toString(),
      } as ListItemDataType;
    });
  }, [
    pageCountModeStep.defaultPages,
    pageCountModeStep.minPages,
    pageCountModeStep.maxPages,
    pageCountModeStep.step,
  ]);

  const items = getPageCounts();
  const selectedIndex = items.findIndex(item => {
    return Number(item.value) === Common.roundToNextMultiple(pageCount, 2);
  });

  return (
    <SelectList
      disabled={disabled}
      items={items}
      selectedIndex={selectedIndex}
      labelledBy={''}
      onChange={event => {
        assistant.dispatch(
          AssistantActions.setPageCount({hasSet: true, pageCount: Number(event.target.value)})
        );
      }}
    />
  );
};

export const PageCountSubStep = ({
  open,
  onClose,
  onAfterClose,
  onBack,
  onContinue,
  isFirstStep,
}: SubStepOptionProps) => {
  const assistant = useAssistant();
  const settings = useSettings();
  const ui = useUiState();
  const {experience} = useExperience();
  const {t} = useTranslation(['assistants', 'dialogs']);
  const {pageCountModeStep} = assistant.state.settings;
  const pageCount = useSelector(TotalPageCountSelector);

  const onChangePageCount = (hasSetPageCount: boolean, pageCount: number) => {
    assistant.dispatch(
      AssistantActions.setPageCount({hasSet: hasSetPageCount, pageCount: pageCount})
    );
  };

  const hasPricedPaperComponent =
    settings.state.price.showPrice &&
    assistant
      .getPricingEngine(AIMode.Off)
      .hasPricedPaperComponent(
        pageCountModeStep.minPages,
        pageCountModeStep.maxPages,
        pageCountModeStep.step
      );
  const pricingMessage = hasPricedPaperComponent && t('*:str_MessagePriceCalculatedLater');

  return (
    <Dialog
      id="assistant-pictures-substep"
      open={open}
      width={ui.state.isSmallScreen ? 400 : 660}
      height={ui.state.isSmallScreen ? 660 : 350}
      afterClose={() => onAfterClose()}
      theme={'tpx-theme-dialogs'}
      themeTip="Dialog"
      title={t('dialogs:str_TitleDeterminePageCount', {
        defaultValue: 'Choose how to determine the number of pages',
      })}
      showTitle={false}
      onClose={onClose}
    >
      <DialogContent>
        <div className={'flex flex-1 sm:flex-col sm:space-y-sm lg:space-x-sm h-full w-full'}>
          {'ccc' === experience.WIZARD.pageCountModeStep.defaultMode && (
            <CheckPanel
              title={t('*:str_MessageIntelligentlyDecideNumberOfPages')}
              titleSize={2}
              onClick={() => {
                onChangePageCount(false, -1);
              }}
              pressed={!assistant.state.setPageCount}
              content={pricingMessage || undefined}
              className={classNames('flex-1', ui.state.isSmallScreen && '[&>div]:pt-lg')}
            />
          )}
          <CheckPanel
            title={t('*:str_MessageSetMyOwnPageCount')}
            titleSize={2}
            onClick={() => {
              if (!assistant.state.setPageCount) {
                onChangePageCount(true, pageCount);
              }
            }}
            pressed={assistant.state.setPageCount}
            className={classNames('flex-1', ui.state.isSmallScreen && '[&>div]:pt-lg')}
            content={
              (
                <PageCountDropDown
                  hasPricedPaperComponent={hasPricedPaperComponent}
                  disabled={!assistant.state.setPageCount}
                />
              ) as any
            }
          />
        </div>
      </DialogContent>
      <DialogFooter
        alignment={!ui.state.isSmallScreen && isFirstStep ? 'between' : 'auto'}
        isSmallScreen={ui.state.isSmallScreen}
      >
        <Button
          buttonStyle={ui.state.isSmallScreen ? 'link' : 'negative'}
          label={t('*:str_ButtonClose')}
          onClick={() => {
            onClose();

            assistant.dispatch(
              AssistantActions.setPageCount({
                hasSet: assistant.state.setPageCount,
                pageCount: assistant.state.originalPageCount,
              })
            );
          }}
        />
        {!isFirstStep && (
          <Button buttonStyle="tertiary" label={t('*:str_LabelBack')} onClick={onBack} />
        )}
        <Button
          buttonStyle="primary"
          label={t('*:str_ButtonContinue')}
          onClick={() => onContinue()}
        />
      </DialogFooter>
    </Dialog>
  );
};
